import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, GraduationCapIcon, LoaderIcon } from 'lucide-react';
import { useFormState } from '../hooks/useFormState';
import { useLeadTracking } from '../hooks/useLeadTracking';
import { usePageMeta } from '../hooks/usePageMeta';
import { formatPhone, stripPhone } from '../utils/phoneFormat';
import { readMatchParams, readProfileParams } from '../utils/matchParams';
import { sanitizeTcpaHtml } from '../utils/sanitizeHtml';
import { DEFAULT_TCPA_DISCLOSURE } from '../data/tcpaDisclosure';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ACCENT, SelectField, StageProgress, StepCard } from '../components/form/FormStepUI';
import { TCPADisclosure } from '../components/TCPADisclosure';
import { TrackingNotice } from '../components/TrackingNotice';
import { trackLeadConversion } from '../utils/conversion';
import { areaFromProgram, clicksSearch, leadsSearch, tcpaPost, tcpaSearch, type ContactProfile, type MMSchool, type SearchProfile, type TCPAResult } from '../services/mediaMatchers';
// Step option lists
// Labels match the partner's value mapping, so the codes sent are unambiguous.
const educationLevels = ['High School Diploma / GED', 'Some High School', 'Some College', "Associate's Degree", "Bachelor's Degree", "Master's Degree", 'Doctorate'];
function getGradYearOptions(): string[] {
  const currentYear = new Date().getFullYear();
  const years: string[] = [];
  // Anything older than 60 years is rejected by the partner, so the list stops
  // there and collapses into a single "Before 1965" option.
  for (let y = currentYear + 2; y >= currentYear - 60; y--) {
    years.push(String(y));
  }
  years.push('Before 1965');
  return years;
}
const gradYearOptions = getGradYearOptions();
const startTimeframes = ['Immediately', '1–3 months', '3–6 months', '6–12 months', 'More than a year', 'Undecided'];
export function RequestInformationForm() {
  usePageMeta('Request Information | Find Online Degree Programs', 'Complete a quick form to get matched with accredited online degree programs.');
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state || {}) as {
    degreeInterest?: string;
    areaOfStudy?: string;
    specialization?: string;
  };
  // Fall back to URL params so a direct anchor link lands here pre-filled.
  const urlPrefill = readMatchParams();
  const profilePrefill = readProfileParams();
  const prefill = {
    degreeInterest: navState.degreeInterest || urlPrefill.degreeInterest,
    // The area is derivable from the program id when it isn't given.
    areaOfStudy: navState.areaOfStudy || urlPrefill.areaOfStudy || areaFromProgram(urlPrefill.specialization),
    specialization: navState.specialization || urlPrefill.specialization,
    ...profilePrefill
  };
  const {
    formData,
    errors,
    updateFormData,
    updateMultiple,
    validateUniStep3
  } = useFormState(prefill);
  const [step, setStep] = useState(1);
  const [zipLoading, setZipLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stepError, setStepError] = useState<string>('');
  // Mounts the LeadiD and TrustedForm scripts, so their hidden inputs are
  // populated before any Media Matchers call fires.
  const {
    leadIdToken,
    trustedFormUrl
  } = useLeadTracking();

  // --- Media Matchers state -------------------------------------------------
  /** The PII step. The searches must resolve before it renders. */
  const CONTACT_STEP = 5;
  /** Location — the last step that contributes to the search payload. */
  const LOCATION_STEP = 4;
  const [leads, setLeads] = useState<MMSchool[] | null>(null);
  const [tcpa, setTcpa] = useState<TCPAResult | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tcpaFailed, setTcpaFailed] = useState(false);
  const searchFired = useRef(false);
  const buildProfile = useCallback((): SearchProfile => ({
    degreeInterest: formData.degreeInterest,
    areaStudy: formData.areaOfStudy,
    mProgram: formData.specialization,
    currentEducation: formData.currentEducation,
    gradYear: formData.gradYear,
    startTimeframe: formData.startDate,
    militaryAffiliation: formData.isMilitary,
    zipCode: formData.zipCode.replace(/\D/g, ''),
    address: formData.address,
    city: formData.city,
    state: formData.state
  }), [formData]);
  const runSearches = useCallback(async () => {
    setSearchLoading(true);
    setTcpaFailed(false);
    const profile = buildProfile();
    // Both fire in parallel — neither depends on the other.
    const [leadsResult, tcpaResult] = await Promise.all([leadsSearch(profile), tcpaSearch(profile)]);
    setLeads(leadsResult || []);
    setTcpa(tcpaResult);
    if (!tcpaResult) setTcpaFailed(true);
    setSearchLoading(false);
  }, [buildProfile]);

  // Safety net: if the visitor reaches the PII step without the searches
  // having run (a restored session, say), fire them on arrival.
  useEffect(() => {
    if (step !== CONTACT_STEP || searchFired.current) return;
    searchFired.current = true;
    runSearches();
  }, [step, runSearches]);
  const retrySearches = () => {
    searchFired.current = true;
    runSearches();
  };
  const lookupZip = useCallback(async (zip: string) => {
    if (zip.length < 5) return;
    setZipLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (res.ok) {
        const data = await res.json();
        const place = data.places?.[0];
        if (place) {
          updateMultiple({
            city: place['place name'] || '',
            state: place['state abbreviation'] || ''
          });
        }
      }
    } catch {
      /* ignore */
    } finally {
      setZipLoading(false);
    }
  }, [updateMultiple]);
  const goBack = () => {
    setStepError('');
    if (step === 1) {
      navigate('/get-matched');
    } else {
      setStep(step - 1);
    }
  };
  const goNext = () => {
    setStepError('');
    // Per-step validation
    if (step === 1 && !formData.currentEducation) {
      setStepError('Please select your highest level of education.');
      return;
    }
    if (step === 2 && !formData.gradYear) {
      setStepError('Please select your graduation year.');
      return;
    }
    if (step === 3 && !formData.startDate) {
      setStepError('Please select when you would like to start.');
      return;
    }
    if (step === LOCATION_STEP) {
      if (!formData.address.trim()) {
        setStepError('Please enter your street address.');
        return;
      }
      if (!formData.zipCode.trim() || formData.zipCode.replace(/\D/g, '').length < 5) {
        setStepError('Please enter a valid 5-digit ZIP code.');
        return;
      }
      // Location is the last input the search payload needs, so both Searches
      // fire here — before the PII step renders — and the TCPA disclaimer is
      // already in hand when the consent checkbox appears.
      if (!searchFired.current) {
        searchFired.current = true;
        runSearches();
      }
    }
    setStep(step + 1);
  };
  const handleSubmit = async () => {
    setStepError('');
    if (!validateUniStep3()) return;
    // Without the TCPA record there is no consent to post against.
    if (!tcpa) {
      setTcpaFailed(true);
      setStepError('We could not load the consent details. Please retry before submitting.');
      return;
    }
    setIsSubmitting(true);
    const profile = buildProfile();
    const contact: ContactProfile = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      email: formData.email.trim(),
      phone: stripPhone(formData.phone)
    };
    // TCPA Post first, then Clicks Search excluding the brand that just took
    // consent, so the carousel has results ready.
    const posted = await tcpaPost(tcpa.mRecordID, contact);
    // Conversion fires on confirmation, not on the click.
    if (posted) trackLeadConversion({
      source: 'tcpa'
    });
    const clicks = await clicksSearch(profile, contact, tcpa.mBrandID);
    setIsSubmitting(false);
    navigate('/matches', {
      state: {
        leads: leads || [],
        clicks: clicks || [],
        profile,
        contact,
        disclosure: tcpa.disclosure,
        excludeBrand: tcpa.mBrandID
      }
    });
  };
  // Card content for each step
  const renderStepCard = () => {
    if (step === 1) {
      return <StepCard title="What is your highest level of education?" onContinue={goNext} ctaLabel="Continue" info="Your education background helps schools understand where you are in your academic journey and recommend the right program level." error={stepError} data-id="element-2807" data-mp="1hbeblew4t5aj">
          <SelectField value={formData.currentEducation} onChange={v => updateFormData('currentEducation', v)} options={educationLevels.map(l => ({
          label: l,
          value: l
        }))} data-id="element-2808" data-mp="36tb95k96yhmm" />
        </StepCard>;
    }
    if (step === 2) {
      return <StepCard title="When did you graduate high school?" onContinue={goNext} ctaLabel="Continue" info="Knowing when you graduated helps us verify eligibility and connect you with programs that fit your timeline." error={stepError} data-id="element-2809" data-mp="3uj5ms41c4a7x">
          <SelectField value={formData.gradYear} onChange={v => updateFormData('gradYear', v)} options={gradYearOptions.map(y => ({
          label: y,
          value: y
        }))} data-id="element-2810" data-mp="3gjxjayy52fxx" />
        </StepCard>;
    }
    if (step === 3) {
      return <StepCard title="When would you like to start classes?" onContinue={goNext} ctaLabel="Continue" info="Your preferred start date helps us find programs with upcoming enrollment windows that work for your schedule." error={stepError} data-id="element-2811" data-mp="7yitw3k8w72p">
          <SelectField value={formData.startDate} onChange={v => updateFormData('startDate', v)} options={startTimeframes.map(t => ({
          label: t,
          value: t
        }))} data-id="element-2812" data-mp="byj75kvlyc86" />
        </StepCard>;
    }
    if (step === 4) {
      return <StepCard title="Where are you located?" onContinue={goNext} ctaLabel="Continue" info="Your location helps us find programs available in your area and determine eligibility for state-specific financial aid." error={stepError} data-id="element-2813" data-mp="zo41t114ixxa">
          <div className="space-y-5" data-id="element-2814" data-mp="2xghstfzdtypr">
            <div data-id="element-2815" data-mp="2nb02aj8stx4r">
              <label htmlFor="address" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2816" data-mp="2nahx2x45s8kp">
                Street Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2817" data-mp="2dl6qbh4qvfzz">
                  *
                </span>
              </label>
              <input id="address" type="text" value={formData.address} onChange={e => updateFormData('address', e.target.value)} placeholder="123 Main St" className="block w-full px-4 py-3 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-0" style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2818" data-mp="23mqytd1t24py" />
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" style={{
            backgroundColor: "#FAFBFE"
          }} data-id="element-2819" data-mp="3tyf4e8zsoma5">
              <label htmlFor="zipCode" className="flex items-center gap-2 text-sm font-bold text-gray-800 mb-2" data-id="element-2820" data-mp="3ri6yct38ysq1">
                <span data-id="element-2821" data-mp="3ijtt2qxt8l0z">📍</span>
                Enter your ZIP Code
              </label>
              <div className="relative" data-id="element-2822" data-mp="3ilogqd0gur8r">
                <input id="zipCode" type="text" value={formData.zipCode} onChange={e => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                updateFormData('zipCode', val);
                if (val.length === 5) lookupZip(val);
              }} placeholder="e.g. 90210" maxLength={5} className="block w-full px-4 py-3 text-base border-2 rounded-md bg-white focus:outline-none" style={{
                borderColor: ACCENT
              }} data-id="element-2823" data-mp="3dlsdwh3i95us" />
                {zipLoading && <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-2824" data-mp="dysrwi5sdat">
                    <LoaderIcon className="w-4 h-4 animate-spin text-gray-500" data-id="element-2825" data-mp="xue9afx0dwku" />
                  </div>}
              </div>
              {formData.city && formData.state && <p className="text-xs text-gray-600 mt-2" data-id="element-2826" data-mp="10eamuywo1dv6">
                  📌 {formData.city}, {formData.state}
                </p>}
            </div>
          </div>
        </StepCard>;
    }
    if (step === 5) {
      const locationLine = formData.city && formData.state ? `${formData.city}, ${formData.state}` : '';
      return <StepCard title="Last step! Where should we send your matches?" subtitle={`We'll match you with top programs in ${locationLine}.`} onContinue={handleSubmit} ctaLabel={isSubmitting ? 'Submitting...' : 'See Matching Colleges'} ctaIcon={<GraduationCapIcon className="w-5 h-5 ml-2 inline-block" data-id="element-2828" data-mp="1t3836whbh0la" />} isSubmitting={isSubmitting || searchLoading} error={stepError} data-id="element-2827" data-mp="1f98efpjzpzb7">
          <div className="space-y-5" data-id="element-2829" data-mp="231oxo3fgrxc3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2830" data-mp="232qlz9lhqgbr">
              <div data-id="element-2831" data-mp="273iw0526sp93">
                <label htmlFor="firstName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2832" data-mp="2730q0gpq2wnz">
                  First Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2833" data-mp="2zrhlqhvl5ddn">
                    *
                  </span>
                </label>
                <input id="firstName" type="text" value={formData.firstName} onChange={e => updateFormData('firstName', e.target.value)} placeholder="John" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.firstName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2834" data-mp="39jdklv5hx7ix" />
                {errors.firstName && <p className="mt-1 text-xs text-red-600" data-id="element-2835" data-mp="dkwafsduezxy">
                    {errors.firstName}
                  </p>}
              </div>
              <div data-id="element-2836" data-mp="hn9iing2b9xw">
                <label htmlFor="lastName" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2837" data-mp="hkdh2p50a34p">
                  Last Name{' '}
                  <span style={{
                  color: ACCENT
                }} data-id="element-2838" data-mp="8mbieg8qvmkl">
                    *
                  </span>
                </label>
                <input id="lastName" type="text" value={formData.lastName} onChange={e => updateFormData('lastName', e.target.value)} placeholder="Doe" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.lastName ? 'border-red-400' : 'border-gray-300'}`} style={{
                '--tw-ring-color': ACCENT
              } as React.CSSProperties} data-id="element-2839" data-mp="119nvlzt0mi18" />
                {errors.lastName && <p className="mt-1 text-xs text-red-600" data-id="element-2840" data-mp="1jyhl8qeib9pi">{errors.lastName}</p>}
              </div>
            </div>

            <div data-id="element-2841" data-mp="1fw4i89r59pwf">
              <label htmlFor="email" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2842" data-mp="1fwmkbn0hmyc9">
                Email Address{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2843" data-mp="166bfzzcw6n4u">
                  *
                </span>
              </label>
              <input id="email" type="email" value={formData.email} onChange={e => updateFormData('email', e.target.value)} placeholder="john@example.com" className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.email ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2844" data-mp="w9ydqd3a70yh" />
              {errors.email && <p className="mt-1 text-xs text-red-600" data-id="element-2845" data-mp="3cuk6vjpesj0e">{errors.email}</p>}
            </div>

            <div data-id="element-2846" data-mp="2g6nnk72l79lh">
              <label htmlFor="phone" className="block text-sm font-bold text-gray-800 mb-1.5" data-id="element-2847" data-mp="2g776nr3z4413">
                Phone Number{' '}
                <span style={{
                color: ACCENT
              }} data-id="element-2848" data-mp="2p4zk0pq4x1lu">
                  *
                </span>
              </label>
              <input id="phone" type="tel" value={formData.phone} onChange={e => updateFormData('phone', formatPhone(e.target.value))} placeholder="555-123-4567" maxLength={13} className={`block w-full px-4 py-3 text-base border rounded-md focus:outline-none focus:ring-2 ${errors.phone ? 'border-red-400' : 'border-gray-300'}`} style={{
              '--tw-ring-color': ACCENT
            } as React.CSSProperties} data-id="element-2849" data-mp="1wi548649iqep" />
              {errors.phone && <p className="mt-1 text-xs text-red-600" data-id="element-2850" data-mp="18t6r5xmqenkd">{errors.phone}</p>}
            </div>

            {/* Consent language comes from the TCPA Search response, so the
                text shown is the text the certificate records. */}
            {searchLoading ? <div className="flex items-center gap-2 text-sm text-gray-500 pt-2" data-id="element-2851" data-mp="1wihhsmadb1ks">
                <LoaderIcon className="w-4 h-4 animate-spin" data-id="element-2852" data-mp="1wg51edk9cz9u" />
                Loading consent details...
              </div> : tcpaFailed ? <div className="rounded-lg border border-red-200 bg-red-50 p-4" data-id="element-2853" data-mp="2b683nor35no7">
                <p className="text-sm text-red-800 font-medium" data-id="element-2854" data-mp="2g4t0rfb1jr8f">
                  We couldn't load the consent details.
                </p>
                <p className="text-xs text-red-600 mt-1" data-id="element-2855" data-mp="2248kapslm7i3">
                  This is required before we can submit your request.
                </p>
                <button type="button" onClick={retrySearches} className="mt-3 inline-flex items-center px-4 py-2 rounded-md bg-white border border-red-300 text-sm font-bold text-red-700 hover:bg-red-100 transition-colors duration-200" data-id="element-2856" data-mp="1seyoozyjq6p8">
                  Retry
                </button>
              </div> : <TCPADisclosure checked={formData.tcpaConsent} onChange={checked => updateFormData('tcpaConsent', checked)} error={errors.tcpaConsent} checkboxClassName="mt-1 h-4 w-4 rounded border-gray-300 cursor-pointer flex-shrink-0 accent-[#1668E0]" data-id="element-2857" data-mp="19g2z61xmughq">
                {/* tcpaDisclaimer is partner-supplied HTML, so it is
                    sanitized rather than rendered as plain text. */}
                {tcpa?.disclosure ? <span dangerouslySetInnerHTML={{
              __html: sanitizeTcpaHtml(tcpa.disclosure)
            }} data-id="element-2858" data-mp="18jxj7chionf2" /> : DEFAULT_TCPA_DISCLOSURE}
              </TCPADisclosure>}
          </div>
        </StepCard>;
    }
    return null;
  };
  return <div className="min-h-screen flex flex-col bg-gray-50" data-id="element-2859" data-mp="1rjbdxwn2m26m">
      <Header data-id="element-2860" data-mp="1rhqgnz9dg552" />
      <main className="flex-1 py-6 sm:py-10 px-4" data-id="element-2861" data-mp="1rh70amzddot5">
        <div className="max-w-3xl mx-auto" data-id="element-2862" data-mp="2f8maf1xe1aey">
          {/* Stage progress. Step 1 of the funnel is program selection on the
              start page, so the questionnaire begins at step 2. */}
          <StageProgress currentStep={step + 1} data-id="element-2863" data-mp="2tdmjwow8fd05" />

          {/* Back button */}
          <div className="mt-2 mb-4" data-id="element-2864" data-mp="2pd2kfeo6rxau">
            <button type="button" onClick={goBack} className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors" data-id="element-2865" data-mp="2pc1bpgun7mwa">
              <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-2866" data-mp="2y5wfolk137ac" />
              Back
            </button>
          </div>

          {/* Step card */}
          <AnimatePresence mode="wait" data-id="element-2867" data-mp="26e85o5oq2wuo">
            <motion.div key={step} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.2
          }} data-id="element-2868" data-mp="26f9t79mxdbta">
              {renderStepCard()}
            </motion.div>
          </AnimatePresence>

          <div className="mt-8" data-id="element-2869" data-mp="2obw1br2esf0">
            <TrackingNotice data-id="element-2870" data-mp="2rg6urz1cmen" />
          </div>
        </div>
      </main>
      <Footer data-id="element-2871" data-mp="6p44i2s44ta8" />
    </div>;
}